import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export function Reveal({ children, delay = 0, className = "", as = "div" }) {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={fadeUp}
      custom={delay}
    >
      {children}
    </M>
  );
}

export function SectionHeader({ eyebrow, title, lead, align = "left", light = false }) {
  const alignCls = align === "center" ? "text-center mx-auto items-center" : "text-left items-start";
  return (
    <div className={`flex flex-col ${alignCls} max-w-3xl gap-4`}>
      {eyebrow && <span className="eyebrow" data-testid="section-eyebrow">{eyebrow}</span>}
      <h2 className={`text-[30px] md:text-[42px] font-800 leading-[1.12] text-balance ${light ? "text-white" : "text-brand-ink"}`}>{title}</h2>
      {lead && <p className={`text-[17px] md:text-[19px] leading-relaxed ${light ? "text-white/75" : "text-brand-muted"}`}>{lead}</p>}
    </div>
  );
}

export function PrimaryButton({ to, href, children, className = "", testid, onClick, type }) {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary px-6 py-3.5 font-heading font-600 text-[15px] text-white shadow-sm transition-colors duration-200 hover:bg-[#0F5541] active:bg-[#0B4435] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg disabled:opacity-60 ${className}`;
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
    </>
  );
  if (to) return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
  if (href) return <a href={href} data-testid={testid} className={cls}>{inner}</a>;
  return <button type={type || "button"} onClick={onClick} data-testid={testid} className={cls}>{inner}</button>;
}

export function SecondaryButton({ to, href, children, className = "", testid, onClick }) {
  const cls = `inline-flex items-center justify-center gap-2 rounded-full border border-brand-primary bg-transparent px-6 py-3.5 font-heading font-600 text-[15px] text-brand-primary transition-colors duration-200 hover:bg-brand-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
  if (href) return <a href={href} data-testid={testid} className={cls}>{children}</a>;
  return <button type="button" onClick={onClick} data-testid={testid} className={cls}>{children}</button>;
}

export function Waveform({ className = "", bars = 40, tone = "#176B52" }) {
  return (
    <div className={`flex items-center gap-[3px] ${className}`} aria-hidden="true">
      {Array.from({ length: bars }).map((_, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full motion-safe:animate-[wave_1.6s_ease-in-out_infinite]"
          style={{
            height: `${8 + (Math.sin(i * 0.9) * 0.5 + 0.5) * 28}px`,
            background: tone,
            opacity: 0.25 + (Math.sin(i) * 0.5 + 0.5) * 0.6,
            animationDelay: `${(i % 10) * 0.09}s`,
          }}
        />
      ))}
    </div>
  );
}

export function Marquee({ items }) {
  const doubled = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-brand-border bg-brand-evergreen py-5">
      <div className="flex w-max motion-safe:animate-marquee motion-reduce:animate-none">
        {doubled.map((t, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 font-heading text-[15px] font-600 uppercase tracking-[0.18em] text-white/80">
            {t}
            <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
          </span>
        ))}
      </div>
    </div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="container-page pt-6">
      <ol className="flex flex-wrap items-center gap-2 text-[13px] text-brand-muted">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2">
            {it.to ? <Link to={it.to} className="hover:text-brand-primary transition-colors">{it.label}</Link> : <span className="text-brand-ink font-500">{it.label}</span>}
            {i < items.length - 1 && <span className="text-brand-border">/</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}
